<!-- Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved. -->

<template>
  <div class="container">
    <h2>
      {{ $t("auth.mfa.title") }}
    </h2>
    <div class="description">
      {{ $t("auth.mfa.description") }}
    </div>
    <form @submit.prevent>
      <input-comp
        autofocus
        id="code"
        name="code"
        ref="code"
        placeholder=" "
        :label="$t('auth.mfa.code')"
        class="input-group--placeholder"
        v-model="formData.code"
        v-model:errorMessages="codeErrors"
        :validationRules="[required, min(4)]"
      ></input-comp>
      <div class="options">
        <checkbox-comp
          v-model="formData.remember"
          :label="$t('auth.mfa.remember')"
          tSel="rememberMyBrowserCheckbox"
          ></checkbox-comp>
      </div>
      <div class="columns">
        <button-comp
          id="submitButton"
          @click="signIn"
          type="submit"
          legacyOrange
          large
        >
          {{ $t("common.continue") }}
        </button-comp>
        <button-comp
          class="columns--cancel"
          @click="cancel"
          anchorStyle
        >
          {{ $t("common.cancel") }}
        </button-comp>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { mapGetters, mapState } from 'vuex';
import { getterTypes } from '@/store/geolocation';
import { postSignInAuthenticatorAsync } from '@/api/userApi';
import { isError } from '@/utils/result';
import { isAbortError } from '@/utils/apiUtils';
import analytics from '@/utils/analytics';
import { IState } from '@/store';
import { required, min, formValidator, IErrorMessage } from '@/val/customValidation';

export default {
  setup() {
    return {
      required,
      min,
      formValidator,
    };
  },
  name: 'signin-mfa-view',
  data() {
    return {
      formData: {
        code: '',
        remember: false,
      },
      codeErrors: [] as IErrorMessage[],
    };
  },
  computed: {
    ...mapState({
      countries: (state: any) => (state as IState).geolocation.countries,
      linkGroups: (state: any) => (state as IState).geolocation.linkGroups,
    }),
    ...mapGetters({
      countryByCountryCode: getterTypes.COUNTRY_BY_CODE,
    }),
    query() {
      if (this.$route && this.$route.query) {
        return this.$route.query;
      }
      return {};
    },
  },
  methods: {
    async signIn() {
      const valid = this.formValidator(this, ['code']);

      if (valid.length === 0) {
        const signInResponse = await postSignInAuthenticatorAsync({
          returnUrl: this.query.returnUrl as string,
          ...this.formData,
        });
        if (isAbortError(signInResponse)) {
          return;
        }

        if (isError(signInResponse)) {
          this.codeErrors.push({
            id: 'code',
            validation: '',
            errorMessageText: this.$t('auth.mfa.error'),
          });
        } else if (signInResponse.result === 'Success') {
          analytics.track('Sign In Completed', {
            email: signInResponse.email,
            title: signInResponse.title,
            firstName: signInResponse.firstName,
            lastName: signInResponse.lastName,
            country: signInResponse.country,
            region: this.countryByCountryCode(
              signInResponse.country
            ).region,
            phone: signInResponse.phoneNumber,
            postalCode: signInResponse.postalCode,
            ecomLanguage: signInResponse.locale,
            ecomUserId: signInResponse.userId,
          });
          window.location.assign(signInResponse.returnUrl);
        } else {
          window.location.assign(signInResponse.returnUrl);
        }
      }
    },
    cancel() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
form {
  margin-top: 50px;
  max-width: 650px;
}

.description {
  position: relative;
  top: 1em;
  font-weight: 0;
  color: $gray-med-3;
  font-size: 14px;
}

.remember {
  margin: 0;
  padding: 0;
  font-size: 14px;
  font-weight: bold;
  color: $primary-gray;
}

.columns {
  max-width: 650px;
  padding: 4em 0;
  display: flex;
  justify-content: flex-start;

  &--cancel {
    margin: 0 3em;
    font-weight: 0;
  }
}

.options {
  display: flex;
  flex-direction: row;
  max-width: 400px;

  :deep(.v-input--selection-controls) {
    margin-top: -8px;
  }

  :deep(label) {
    font-size: 14px;
    font-weight: bold;
    color: $primary-gray;
  }
}

a {
  position: relative;
  top: 0.9em;
  margin: 3em;
  font-weight: 0;
  color: $proto-blue;
}
</style>
